import { ref } from "vue";
import { defineStore } from "pinia";
import { useTablesStore } from "@/stores/tables";
import { useUserStore } from "@/stores/user";
import { useViewSettingsStore } from "@/stores/viewSettings";
import ApiService from "@/core/services/ApiService";
import axios from "axios";
import JwtService from "@/core/services/JwtService";
import DBConfigService from "@/core/services/DBConfigService";
import CryptoService from "@/core/services/CryptoService";
import { httpsSafeUrl } from "@/core/helpers/httpsSafeUrl";
import { bootDiag } from "@/core/helpers/bootDiag";
/**
 * Ein Login-/Renew-Datensatz, wie er zwischen Anmeldeformular, server_auth und
 * der `dbconfig`-Zeile in IndexedDB hin- und herwandert.
 *
 * Das ist bewusst EIN Typ für drei Quellen — der Code reicht dieselben Objekte
 * unverändert durch (`setAuth(result, result)`), deshalb wäre eine Aufspaltung
 * eine Lüge über den Ist-Zustand. Alles außer den Kernfeldern ist optional:
 * welche Felder gesetzt sind, hängt davon ab, ob der Datensatz vom Formular,
 * vom Server oder aus der lokalen Zeile stammt.
 *
 * `cs` ist ein JSON-**String**, kein Array — server_auth legt die Firmenliste
 * so in den JWT-Nutzdaten ab. Wer sie als Liste braucht, muss parsen; siehe die
 * Warnung in `getbrowsercache()`.
 */
export interface User {
  name: string;
  surname: string;
  em: string;
  pw: string;
  at: string;
  id: string;
  di: string;
  pk: string;
  /** Refresh-Token. Nur in der Serverantwort und in der lokalen Zeile. */
  rt?: string;
  /** Firmenliste als JSON-String, z. B. `'["<uuid>","<uuid>"]'`. */
  cs?: string;
  /** Aktive Firma. Einmal bei `insertnewuser` gesetzt, danach nie wieder. */
  ac?: string;
  /** Privater RSA-Schlüssel dieses Geräts (lokale Zeile). */
  sk?: string;
  /** Frisch erzeugtes Schlüsselpaar — nur auf dem Neugeräte-Pfad gesetzt. */
  publickey?: string;
  privatekey?: string;
  /** Gerätekennung für die Registrierung eines neuen Geräts. */
  deviceinfo?: {
    id: string;
    appCodeName: string;
    appName: string;
    userAgent: string;
    platform: string;
  };
  /**
   * Zustimmungsstand, den server_auth der Login- und der Renew-Antwort
   * beilegt. Optional, weil ältere Serverstände das Feld nicht kennen.
   */
  consent?: { required: LegalDocumentMeta[] };
}

/** Kopfdaten eines Rechtstextes, wie sie server_auth in `consent.required` meldet. */
export interface LegalDocumentMeta {
  id: string;
  doc_type: string;
  audience: string;
  lang: string;
  version: number;
  title: string;
  published_at?: string | null;
}

export const useAuthStore = defineStore("auth", () => {
  const errors = ref({});
  const user = ref<User>({} as User);
  const tablesStore = useTablesStore()
  const userStore = useUserStore()
  const viewSettingsStore = useViewSettingsStore()
  tablesStore.initQueryWorker()
  tablesStore.initServerWorker()
  const passwordwrong = ref(false)
  const authretries = ref(0)
  const isLoggedin = ref(false)
  /**
   * Ist die letzte Token-Erneuerung an einem VORÜBERGEHENDEN Fehler gescheitert?
   *
   * `renewTokenForWorker()` gibt weiterhin nur true/false zurück — drei der vier
   * Aufrufstellen lassen bei false schlicht den Retry aus, für die ändert sich
   * nichts. Nur der 'renewtokenrequired'-Handler in tables.ts leitet auf die
   * Loginseite um, und genau der muss wissen, ob die Sitzung wirklich ungültig
   * ist oder ob server_auth nur gerade nicht an die Datenbank kommt.
   */
  const renewFailedTransiently = ref(false)
  const isAuthenticated = ref(false);
  const userisloggedincheckisrunning = ref(false);
  /**
   * Offene, zustimmungspflichtige Rechtstexte dieses Kontos.
   *
   * Gefüllt aus der Login-Antwort (`consent.required`) und danach von
   * `refreshConsents()`. Solange die Liste nicht leer ist, blendet
   * `ConsentGate.vue` eine blockierende Ebene über die App — ohne Einwilligung
   * kein Zugang, unabhängig davon, über welchen Weg das Konto entstanden ist.
   */
  const pendingConsents = ref<LegalDocumentMeta[]>([]);
  /**
   * Set by login() when the auth server hands back a device id that differs
   * from the one this browser sent — i.e. the device was (re-)registered and
   * the local data set belongs to a device binding that no longer exists.
   * Consumed once by getbrowsercache(), which triggers the full re-sync at the
   * only point where the worker already holds a token.
   */
  let fullResyncPending = false
  function loggedinbyworker(data) {
    console.log('loggedinbyworker')
    console.log(data)
  }
  function getbrowsercache(credentials, data) {
    // Server worker must receive 'init' to call init() which creates the Dexie dbconfig instance.
    // WorkerService only sends init to the query worker — this is the sole trigger for server worker init.
    tablesStore.ServerWorkerSendMessage({ cmd: 'init' })
    // ── `cs` ist ein JSON-STRING, kein Array ────────────────────────────────
    // Der Typecheck hat hier einen echten Fehler freigelegt: `companies` war
    // als '' initialisiert, Zeile 2 wies den ROHEN String `data.cs` zu und nur
    // Zeile 3 parste. Kam der Datensatz vom Server ohne `credentials.cs` —
    // also beim ERSTEN Login auf einem neuen Gerät —, blieb `companies` ein
    // String wie '["<uuid>",…]', und `companies[0]` lieferte das erste
    // ZEICHEN: '['. Das ging als aktive Firma an beide Worker. Da '[' nicht
    // falsy ist, greift auch der Fallback auf `user.vs.lastActiveCompany` im
    // ACLQueryHandler nicht. Beide Quellen werden jetzt gleich behandelt.
    let companies: string[] = []
    let ac = ''
    if (credentials.ac) ac = credentials.ac
    const rawCompanies = !credentials.ac ? (credentials.cs || data.cs) : null
    if (rawCompanies) companies = typeof rawCompanies === 'string' ? JSON.parse(rawCompanies) : rawCompanies
    if (companies.length > 0) ac = companies[0]
    // Protokolliert wird, was WIRKLICH rausgeht — also `companies[0]`. Die Zeile
    // gab vorher `companies[0] || ac` aus und meldete damit die gespeicherte
    // `credentials.ac`, die die Nachricht gar nicht enthält: im iPad-Log stand
    // "ac: 2c67995c…", im Worker kam "company: (none)" an. Ein Log, das einen
    // Rückfall anzeigt, den der Sendeaufruf nicht kennt, verlängert jede Suche.
    //
    // `credentials.ac` ist als Ersatz auch untauglich: es wird einmal bei
    // insertnewuser aus JSON.parse(authUser.cs)[0] gesetzt und danach NIE wieder
    // geschrieben — ein Firmenwechsel kommt dort nicht an. Der Worker füllt die
    // Lücke selbst aus `user.vs.lastActiveCompany` (siehe ACLQueryHandler).
    console.log('[ACL] getbrowsercache → getuseracldata2 — id:', data.id || '(none)', 'di:', data.di || '(none)', 'ac:', companies[0] || '(none — Worker nimmt die zuletzt aktive Company)')
    if (tablesStore.initialimportfromserver) {
      // Send settoken through the guarded helper to avoid double-send with
      // the ACL completion / tableupdate handlers in tables.ts.
      tablesStore.sendTokenToWorkerOnce(data.at, data.rt, data.id, data.di, companies[0])
      tablesStore.QueryWorkerSendMessage({ 'cmd': 'getuseracldata2', 'id': data.id, 'di': data.di, 'ac': companies[0] })
      // Note: getcache will be called after sync completes (see tables.ts line 835)
    } else {
      // Send settoken through the guarded helper to avoid double-send with
      // the ACL completion / tableupdate handlers in tables.ts.
      tablesStore.sendTokenToWorkerOnce(data.at, data.rt, data.id, data.di, companies[0])
      // Load existing tables from IndexedDB into Pinia store
      tablesStore.QueryWorkerSendMessage({ 'cmd': 'getuseracldata2', 'id': data.id, 'di': data.di, 'ac': companies[0] })
      // Note: getcache will be called after sync completes (see tables.ts line 835)
      // No need to call getinitialdata here - incremental sync handles updates
    }
    // ── New device → throw the local data set away and re-sync from scratch ──
    // Sent AFTER settoken on purpose: 'serverrefresh' deletes the data DB and
    // immediately runs datacheck('serverrefresh','init'), which needs the token
    // the line above just delivered. The worker's own guard (serverrefreshvalue)
    // is reset by that same settoken, so this command is never swallowed.
    if (fullResyncPending) {
      fullResyncPending = false
      console.log('[auth] Device was re-registered — requesting a full re-sync')
      tablesStore.ServerWorkerSendMessage({ cmd: 'serverrefresh' })
    }
  }
  /**
   * Decide which company is active after login or a page refresh.
   *
   * Single, ordered path — nothing else may pre-set config.ActiveCompany:
   *   1. the persisted last active company (viewSettings.lastActiveCompany)
   *   2. whatever is already set (in-session switch)
   *   3. the first company of the JWT list
   * …each of them run through resolveActiveCompany(), which never returns an
   * archived company as long as a non-archived candidate exists. An archived
   * company can still be opened deliberately through the CompanySwitcher.
   *
   * Previously ActiveCompany was set to companies[0] synchronously here, which
   * (a) showed an archived company on every refresh when it happened to be
   * first in the JWT and (b) got picked up by the viewSettings watchers and
   * persisted as "last active", so the wrong company stuck permanently.
   */
  async function bootstrapActiveCompany(authUser: any, candidates: any) {
    try {
      if (authUser?.id) await viewSettingsStore.initializeSettings(authUser.id)
      const preferred = viewSettingsStore.lastActiveCompany
        || tablesStore.config.ActiveCompany
        || ''
      if (viewSettingsStore.lastActiveCompany) {
        console.log('🔄 Restoring last active company:', viewSettingsStore.lastActiveCompany)
      }
      const resolved = await DBConfigService.resolveActiveCompany(preferred, candidates)
      if (resolved) {
        if (resolved !== preferred) console.log('🏢 ActiveCompany resolved to:', resolved)
        tablesStore.config.ActiveCompany = resolved
        // getbrowsercache() requested the ACL for candidates[0]; if the
        // resolved company differs, re-request the rights for the right one.
        const list = typeof candidates === 'string' ? JSON.parse(candidates || '[]') : (candidates || [])
        if (Array.isArray(list) && list.length > 0 && list[0] !== resolved && authUser?.id) {
          tablesStore.QueryWorkerSendMessage({ cmd: 'getuseracldata2', id: authUser.id, di: authUser.di, ac: resolved })
        }
      }
    } catch (err) {
      console.warn('[auth] bootstrapActiveCompany failed:', err)
    } finally {
      // Release the server worker fallback — it may now fill in a company
      // if (and only if) nothing could be restored.
      tablesStore.markActiveCompanyBootstrapped()
    }
  }
  /**
   * `updateDB` gab es hier als dritten Parameter — er wurde im Rumpf NIE
   * gelesen. Drei der fünf Aufrufer haben ihn trotzdem gesetzt und damit den
   * Eindruck erweckt, man könne das Schreiben in IndexedDB abschalten. Konnte
   * man nie. Entfernt statt mitgeschleppt.
   */
  function setAuth(credentials: User, authUser: User) {
    userStore.setUserData('credentials', credentials)
    if (authUser && authUser.id && authUser.di) {
      userStore.setUserData('id', authUser.id)
      userStore.setUserData('deviceid', authUser.di)
      userStore.setUserData('companies', authUser.cs)

      // ActiveCompany is NOT set here — bootstrapActiveCompany() below owns it
      // (restores the persisted company, never auto-selects an archived one).
    }
    isAuthenticated.value = true;
    isLoggedin.value = true;
    user.value = authUser;
    errors.value = {};
    // Die Login-Antwort trägt den Zustimmungsstand mit. Ältere Server-Stände
    // kennen das Feld nicht — dann bleibt die Liste leer und der Gate holt sie
    // per refreshConsents() nach, statt die App fälschlich freizugeben oder
    // fälschlich zu sperren.
    if (authUser && authUser.consent && Array.isArray(authUser.consent.required)) {
      pendingConsents.value = authUser.consent.required;
    }
    DBConfigService.checkifcompaniesindb(authUser.cs)
      .then(function (newCompanies) {
        // newCompanies is an array of company IDs that were just added to IndexedDB
        if (newCompanies && newCompanies.length > 0) {
          console.log(`✨ ${newCompanies.length} new company/companies detected:`, newCompanies)
          console.log('⚠️ Note: Initial sync will be triggered automatically by server worker')
          // Note: Do NOT trigger getinitialdata here - it causes infinite loops!
          // The server worker will automatically sync when it detects missing data
        }
      })
      .catch(function (error) {
        console.log('Error checking companies in DB:', error);
      })
    DBConfigService.checkifinitialimportdone()
      .then(function (response) {
        tablesStore.changeparameter('initialimportfromserver', response)
      })
    DBConfigService.getuserfromstorage(credentials)
      .then(function (userdata) {
        if (!userdata || !userdata.em) {
          hashPassword(credentials)
            .then(function (password) {
              // Fix: credentials.privatekey is the RSA private key for a new device;
              // authUser.pkey is always undefined (not in server response) — bug fixed.
              // Einmal parsen statt zweimal — und mit Fallback, weil `cs` in der
              // Serverantwort fehlen kann. Ohne den Fallback wirft JSON.parse
              // hier, und der Neugeräte-Login bricht wortlos ab.
              const companyList: string[] = authUser.cs ? JSON.parse(authUser.cs) : []
              DBConfigService.insertnewuser({ id: authUser.id, em: credentials.em, pw: password, at: authUser.at, rt: authUser.rt, pk: credentials.publickey, sk: credentials.privatekey || '', cs: companyList, ac: companyList[0], di: authUser.di, vs: '', kl: true })
              getbrowsercache(credentials, authUser)
              // New device/user: nothing persisted yet → falls back to the first
              // non-archived company of the JWT list.
              bootstrapActiveCompany(authUser, authUser.cs).then(() => {
                tablesStore.initViewSettingsWatchers()
                tablesStore.restoreViewSettings()
              })
            })
        } else {
          // Die Zeile existiert bereits, also läuft insertnewuser({… kl: true})
          // NICHT — und die Serverantwort kennt das Feld `kl` gar nicht. Ohne
          // dieses explizite true bleibt eine einmal auf `kl: false` gelaufene
          // Installation für immer auf der Loginseite: die Anmeldung gilt nur
          // im Speicher und ist nach dem Refresh weg.
          //
          // Bewusst als EIN Schreibvorgang zusammen mit updateuser, nicht als
          // zweiter Aufruf daneben: updateuser liest die Zeile, entschlüsselt,
          // verschlüsselt und schreibt sie GANZ zurück. Ein parallel gesetztes
          // kl=true wird dabei vom mitgeschleppten Altwert wieder überschrieben
          // — und weil updateuser durch Lesen und Krypto langsamer ist, gewinnt
          // es dieses Rennen zuverlässig. Genau so ist der erste Anlauf dieses
          // Fixes gescheitert.
          // Das nachgeschaltete setKeepLoggedIn schließt die letzte Lücke:
          // updateuser überträgt nur Felder, die auf der Zeile EXISTIEREN —
          // fehlt `kl` dort ganz, würde der Merge es überspringen. Verkettet,
          // nicht danebengestellt: die Reihenfolge muss festliegen.
          DBConfigService.updateuser({ ...authUser, kl: true })
            .then(() => DBConfigService.setKeepLoggedIn(authUser.id, true))
          getbrowsercache(credentials, authUser)
          // Restore view settings + the last active company from IndexedDB
          if (authUser.id) {
            bootstrapActiveCompany(authUser, userdata.cs || authUser.cs).then(() => {
              // Initialize watchers for auto-saving changes
              tablesStore.initViewSettingsWatchers()
              // Restore settings for current company
              tablesStore.restoreViewSettings()
            })
          }
        }
      })
    return false
  }

  function setError(error: any) {
    errors.value = { ...error };
  }

  function purgeAuth() {
    isAuthenticated.value = false;
    user.value = {} as User;
    errors.value = [];
    // JwtService.destroyToken();
  }
  function hash(algo, str) {
    return crypto.subtle.digest(algo, new TextEncoder().encode(str))
  }
  function encode64(buff) {
    return btoa(new Uint8Array(buff).reduce((s, b) => s + String.fromCharCode(b), ''))
  }
  async function hashPassword(user) {
    let hashedPassword = await new Promise((resolve, reject) => {
      hash('SHA-256', user.password).then(hashed => {
        resolve(encode64(hashed))
      })
    })
    return hashedPassword
  }
  function arrayBufferToBase64String(arrayBuffer) {
    var byteArray = new Uint8Array(arrayBuffer)
    var byteString = ''
    for (var i = 0; i < byteArray.byteLength; i++) {
      byteString += String.fromCharCode(byteArray[i])
    }
    return btoa(byteString)
  }
  function convertBinaryToPem(binaryData, label) {
    var base64Cert = arrayBufferToBase64String(binaryData)
    var pemCert = "-----BEGIN " + label + "-----\r\n"
    var nextIndex = 0
    var lineLength
    while (nextIndex < base64Cert.length) {
      if (nextIndex + 64 <= base64Cert.length) {
        pemCert += base64Cert.substr(nextIndex, 64) + "\r\n"
      } else {
        pemCert += base64Cert.substr(nextIndex) + "\r\n"
      }
      nextIndex += 64
    }
    pemCert += "-----END " + label + "-----\r\n"
    return pemCert
  }
  function exportPublicKey(keys) {
    return new Promise(function (resolve) {
      crypto.subtle.exportKey('spki', keys.publicKey).
        then(function (spki) {
          resolve(convertBinaryToPem(spki, "RSA PUBLIC KEY"))
        })
    })
  }
  function exportPrivateKey(keys) {
    return new Promise(function (resolve) {
      var expK = crypto.subtle.exportKey('pkcs8', keys.privateKey)
      expK.then(function (pkcs8) {
        resolve(convertBinaryToPem(pkcs8, "RSA PRIVATE KEY"))
      })
    })
  }
  async function exportPemKeys(keys) {
    return new Promise(function (resolve) {
      exportPublicKey(keys).then(function (pubKey) {
        exportPrivateKey(keys).then(function (privKey) {
          resolve({ publicKey: pubKey, privateKey: privKey })
        })
      })
    })
  }
  async function getkeypair() {
    if (!crypto?.subtle) {
      throw new Error('Web Cryptography API unavailable — please open the app via HTTPS.')
    }
    let keyPair = await crypto.subtle.generateKey(
      {
        name: "RSA-OAEP",
        modulusLength: 2048, // Reduced from 4096 for faster generation (2-4s improvement)
        publicExponent: new Uint8Array([1, 0, 1]),
        hash: "SHA-256",
      },
      true,
      ["encrypt", "decrypt"],
    )
    return keyPair
  }
  // Pre-generated device RSA keypair (PEM). Kicked off while the user is
  // still typing on the sign-in form so the (on iPhone 1–2 s) keygen does not
  // run between submit and the login request. Only used for new devices.
  let pregeneratedKeys: Promise<{ publicKey: string; privateKey: string }> | null = null
  async function makeDeviceKeys(): Promise<{ publicKey: string; privateKey: string }> {
    return exportPemKeys(await getkeypair()) as Promise<{ publicKey: string; privateKey: string }>
  }
  function pregenerateDeviceKeys(): void {
    if (pregeneratedKeys || !crypto?.subtle) return
    pregeneratedKeys = makeDeviceKeys()
    pregeneratedKeys.catch(() => { pregeneratedKeys = null })
  }
  async function loginprocess(credentials: User) {
    // Init Datenbanken
    return new Promise((resolve, reject) => {
      DBConfigService.getuserfromstorage(credentials)
        .then(function (userdata) {
          if (!userdata || !userdata.em) {
            (pregeneratedKeys || makeDeviceKeys())
              .then(function (result) {
                credentials.publickey = result.publicKey
                credentials.privatekey = result.privateKey
                resolve(credentials);
              })
              .catch(() => { reject() })
          } else {
            credentials.pk = userdata.pk
            credentials.di = userdata.di
            credentials.sk = userdata.sk  // carry existing private key so login() can init CryptoService
            resolve(credentials);
          }
        })
        .catch(() => { reject() })
    })
  }
  /** Base URL for the standalone auth server.
   * Resolved from VITE_APP_AUTH_URL which is set per-mode in:
   *   .env              → http://localhost:8089  (dev fallback)
   *   .env.staging      → https://api.atlas.max-min.de/auth
   *   .env.production   → https://api.mycrogym.com/auth
   */
  function getAuthUrl(): string {
    const env = import.meta.env.VITE_APP_AUTH_URL
    // httpsSafeUrl: Safari blocks http://localhost:8089 from the HTTPS dev
    // page as mixed content — route through the Vite /auth proxy instead.
    if (env) return httpsSafeUrl(env)
    // No env set (dev): use current origin so the Vite /auth proxy routes to localhost:8089
    return window.location.origin
  }

  function login(credentials: User, privateKey: string, deviceId: string, userId: string) {
    if (deviceId === '') {
      credentials.deviceinfo = {
        id: '',
        appCodeName: navigator.appCodeName,
        appName: navigator.appName,
        userAgent: navigator.userAgent,
        platform: navigator.platform
      }
      credentials.deviceinfo.id = deviceId
    }
    credentials.id = userId || ''
    // The device id this browser is about to present. If the server answers
    // with a DIFFERENT one, it re-registered us (our row was gone server-side)
    // and everything cached locally belongs to a binding that no longer exists.
    const sentDeviceId = credentials.di || ''
    // Login goes to server_auth (not studio server)
    return axios.post(`${getAuthUrl()}/auth/signin/login/`, credentials)
      .then(async function (response) {
        // Initialize CryptoService from the device RSA private key BEFORE saving tokens.
        // credentials.privatekey = newly generated key (new device)
        // credentials.sk = carried from IndexedDB (existing device, set in loginprocess)
        try {
          await DBConfigService.initCryptoFromUser(credentials)
        } catch (cryptoErr) {
          // Crypto init failure must NOT block login — tokens will be stored plaintext
          // and _encryptUserRow will skip encryption gracefully.
          console.warn('[login] CryptoService init failed — tokens stored unencrypted:', cryptoErr)
        }
        // ── Device changed → the local data set is orphaned ──────────────────
        // Only a re-registration counts: a first login (no id sent) has nothing
        // stale to clean up, and an unchanged id means the binding still holds.
        // The companies are marked ii:false BEFORE setAuth so the flag is
        // persisted even if the user closes the tab before the sync finishes —
        // otherwise ii:true plus an emptied data DB leaves the app silently
        // empty forever.
        const receivedDeviceId = response?.data?.di || ''
        if (sentDeviceId && receivedDeviceId && receivedDeviceId !== sentDeviceId) {
          console.warn(`[login] Device re-registered (${sentDeviceId} → ${receivedDeviceId}) — local data set is stale, forcing a full initial sync`)
          try {
            await DBConfigService.resetCompaniesForFullSync()
            fullResyncPending = true
          } catch (resetErr) {
            // Never block the login on this — a stale cache is recoverable via
            // the "Full Sync" entry in the profile menu.
            console.error('[login] Could not mark companies for a full sync:', resetErr)
          }
        }
        setAuth(credentials, response.data)
        return response
      })
      .catch(function (error) {
        if (error && error.response) setError(error.response.data.errors);
      })
  }

  function logout() {
    // Notwendige Gegenseite zum setKeepLoggedIn(true) in setAuth: Solange `kl`
    // dauerhaft auf false festhing, wirkte jeder Logout korrekt, weil der
    // Refresh ohnehin auf der Loginseite endete. Jetzt, wo die Anmeldung die
    // Zeile wirklich markiert, MUSS das Abmelden sie wieder zurücksetzen —
    // sonst meldet ein Reload den gerade Abgemeldeten wieder an.
    DBConfigService.setKeepLoggedIn(null, false)
    CryptoService.destroy() // Clear in-memory AES key
    // Rechte, Company und Worker-Guards der alten Session verwerfen — sonst
    // gelten sie beim nächsten Login weiter, bis die ACL-Ableitung des
    // Query-Workers zurückkommt (SPA-Logout = kein Page-Reload).
    tablesStore.resetSessionState()
    // Offene Zustimmungen gehören zur Sitzung — bliebe die Liste stehen, läge
    // die Sperrebene beim nächsten Login über einem fremden Konto.
    pendingConsents.value = []
    purgeAuth();
  }

  function register(credentials: User) {
    return ApiService.post("register", credentials)
      .then(({ data }) => {
        // Zweimal `data`: setAuth() braucht Anmeldedaten UND Serverantwort,
        // und bei der Registrierung sind das dasselbe Objekt — wie in
        // checkifuserisLoggedin() (`setAuth(result, result)`). Vorher stand
        // hier `setAuth(data)`; der Rumpf greift ab der zweiten Anweisung auf
        // den fehlenden zweiten Parameter zu, die Registrierung endete also
        // sicher in einem TypeError.
        setAuth(data, data);
      })
      .catch(({ response }) => {
        if (response) setError(response.data.errors);
      });
  }

  // Password reset lives on server_auth. 'app' tells the auth server which
  // frontend the request comes from so it mails the correct reset link.
  function forgotPassword(email: string) {
    return axios.post(`${getAuthUrl()}/auth/password/forgot/`, { em: email, app: 'studio' })
      .then(() => {
        setError({});
      })
      .catch(({ response }) => {
        if (response) setError(response.data.errors);
      });
  }

  function validateResetToken(token: string) {
    return axios.post(`${getAuthUrl()}/auth/password/validatetoken/`, { token })
      .then(() => {
        setError({});
        return true;
      })
      .catch(({ response }) => {
        if (response) setError(response.data.errors);
        return false;
      });
  }

  function verifyResetCode(token: string, code: string) {
    return axios.post(`${getAuthUrl()}/auth/password/verifycode/`, { token, code })
      .then(() => {
        setError({});
        return true;
      })
      .catch(({ response }) => {
        if (response) setError(response.data.errors);
        return false;
      });
  }

  // One-shot credentials handoff: after a successful reset the sign-in form
  // prefills email + new password from here (in-memory only, cleared on read).
  const loginPrefill = ref<{ em: string; pw: string } | null>(null);

  function resetPassword(token: string, code: string, password: string) {
    return axios.post(`${getAuthUrl()}/auth/password/reset/`, { token, code, pw: password })
      .then(({ data }) => {
        setError({});
        if (data && data.em) loginPrefill.value = { em: data.em, pw: password };
        return true;
      })
      .catch(({ response }) => {
        if (response) setError(response.data.errors);
        return false;
      });
  }
  /**
   * Laufende Prüfung, damit parallele Aufrufer dieselbe Antwort bekommen.
   *
   * Vorher war `userisloggedincheckisrunning` ein Einweg-Schalter: einmal auf
   * true gesetzt und NIRGENDS zurückgesetzt (auch nicht in logout()). Jeder
   * zweite Aufruf fiel am if vorbei und lieferte implizit `undefined` — was der
   * Router-Guard als "nicht eingeloggt" liest und auf die Loginseite umleitet.
   * Ein einziger fehlgeschlagener erster Versuch vergiftete damit die gesamte
   * Seitenlebenszeit. Jetzt teilen sich Parallelaufrufer die Promise, und der
   * Schalter fällt im finally zurück.
   */
  let authcheckinflight: Promise<any> | null = null
  async function checkifuserisLoggedin() {
    if (authcheckinflight) return authcheckinflight
    userisloggedincheckisrunning.value = true
    authcheckinflight = (async () => {
      try {
        // Bootstrap CryptoService from the device RSA private key (sk field)
        // stored in the user row. Must run before reading encrypted tokens.
        // Lief bisher außerhalb jeder Absicherung: ein Fehler beim Öffnen der
        // Config-DB (Dexie-Upgrade, Primärschlüssel-Migration) hat die ganze
        // Funktion rejected und den Nutzer wortlos ausgeloggt.
        try {
          await DBConfigService.bootstrapCrypto()
        } catch (cryptoErr) {
          // Kein Grund zum Ausloggen: ohne Schlüssel bleiben at/rt verschlüsselt
          // liegen, die Zeile selbst (id, kl, di) ist unverschlüsselt lesbar.
          bootDiag('authcheck.crypto-bootstrap-failed', cryptoErr)
        }

        const result = await DBConfigService.checkifuserisloggedin()
        if (!result) {
          bootDiag('authcheck.no-user-row')
          return false
        }
        isAuthenticated.value = true
        // View settings + last active company are restored inside setAuth →
        // bootstrapActiveCompany (single ordered path). Doing it here too
        // would race that restore and could re-select the wrong company.
        //
        // setAuth hat eine eigene Absicherung: Es ist ein langer Seiteneffekt
        // (Stores, IndexedDB, Company-Auflösung). Wirft irgendein Teilschritt,
        // ändert das nichts daran, dass eine gültige Sitzung in der DB liegt —
        // vorher wurde daraus ein stiller Rauswurf auf die Loginseite.
        try {
          setAuth(result, result)
        } catch (setAuthErr) {
          bootDiag('authcheck.setauth-failed', setAuthErr)
        }
        return result
      } catch (error) {
        // Vorher: `.catch(error => false)` — der Fehler wurde nicht einmal
        // geloggt. Genau deshalb war auf prod nichts zu sehen.
        bootDiag('authcheck.failed', error)
        return false
      } finally {
        userisloggedincheckisrunning.value = false
        authcheckinflight = null
      }
    })()
    return authcheckinflight
  }
  function verifyAuth(loggedinuser) {
    // Verify goes to server_auth (not studio server)
    axios.post(`${getAuthUrl()}/auth/signin/verify/`, { at: loggedinuser.at, id: loggedinuser.id, di: loggedinuser.di })
      .then(function (response) {
        setAuth(loggedinuser, loggedinuser)
      })
      .catch(({ response }) => {
        if (response && response.data && response.data.message === "access_token expired") {
          renewToken(loggedinuser)
          setError(response.data.errors);
        }
        // purgeAuth();
      });
  }

  // Renewal fallback requested by the server worker when it has no plaintext
  // refresh token (user.rt in IndexedDB is an EncryptedBlob and only the main
  // thread holds the CryptoService key). Returns false if a fresh login is
  // required.
  async function renewTokenForWorker(): Promise<boolean> {
    renewFailedTransiently.value = false
    try {
      const row = await DBConfigService.checkifuserisloggedin()
      if (!row) {
        bootDiag('renewforworker.no-user-row')
        return false
      }
      // Kein String heißt: rt liegt noch als EncryptedBlob vor, weil
      // _decryptUserRow die Entschlüsselung still verworfen hat (falscher oder
      // fehlender device_secret). Das ist ein Schlüsselproblem, kein
      // abgelaufenes Token — aber der Aufrufer leitet auf die Loginseite um,
      // also muss der Unterschied wenigstens in der Spur stehen.
      if (typeof row.rt !== 'string' || !row.rt) {
        bootDiag('renewforworker.rt-not-decryptable', `typeof rt = ${typeof row.rt}`)
        return false
      }
      const { data } = await axios.post(`${getAuthUrl()}/auth/signin/renewtoken/`, { rt: row.rt, id: row.id, di: row.di, em: row.em })
      if (!data?.at) {
        bootDiag('renewforworker.no-token-in-response')
        return false
      }
      // Re-encrypts at/rt on write (insertnewuser runs _encryptUserRow)
      await DBConfigService.insertnewuser({ ...row, at: data.at })
      // `id`/`di` MITSCHREIBEN, nicht nur `at`.
      //
      // Läuft die Erneuerung, bevor die Rechte-Ableitung geantwortet hat, ist
      // `datasource` noch das leere Objekt — der Merge ergab dann `{ at }` ohne
      // Benutzer- und Gerätekennung. Genau daran ist auf Staging der
      // Wiederholungsversuch von `useAuthorizedFetch` gescheitert: frisches
      // Token, leere X-Device-Id, wieder 401. Die Zeile `row` trägt beide
      // Werte, sie werden zwei Zeilen weiter oben schon an server_auth
      // geschickt.
      userStore.setUserData('datasource', { ...userStore.Userdata.datasource, id: row.id, di: row.di, em: row.em, at: data.at })
      tablesStore.ServerWorkerSendMessage({ 'cmd': 'settoken', 'token': data.at, 'rt': row.rt, 'userid': row.id, 'deviceid': row.di, 'refresh': false, 'activecompany': tablesStore.config.ActiveCompany })
      // Erneuert der Worker selbst, verkündet er das Ergebnis auf `studio_auth`
      // (server.worker.ts, getnewaccesstoken). Übernimmt der Hauptthread — weil
      // nur er den CryptoService-Schlüssel für `rt` hat —, blieb der Kanal
      // bisher stumm: `waitForTokenRefresh` in chat.server.ts lief dann in
      // seinen 12-s-Timeout, bevor es den Token doch noch aus IndexedDB las.
      try {
        const ch = new BroadcastChannel('studio_auth')
        ch.postMessage({ cmd: 'tokenrefreshed', at: data.at })
        ch.close()
      } catch { /* BroadcastChannel fehlt (alter WebView) — IDB bleibt der Weg */ }
      return true
    } catch (error: any) {
      // 5xx = server_auth erreicht die Datenbank nicht; keine Antwort = Netz weg
      // oder Dienst startet neu. Beides sagt nichts über die Gültigkeit der
      // Sitzung aus. 422 dagegen heißt: Refresh-Token abgelaufen — echter Fall.
      const status = error?.response?.status
      renewFailedTransiently.value = status === undefined || status >= 500
      bootDiag(
        renewFailedTransiently.value ? 'renewforworker.transient' : 'renewforworker.request-failed',
        error
      )
      return false
    }
  }

  function renewToken(loggedinuser) {
    // checkifuserisloggedin() liest die einzige kl=true-Zeile und nimmt
    // KEINE Argumente — das übergebene { id } wurde stillschweigend verworfen.
    DBConfigService.checkifuserisloggedin()
      .then(function (result) {
        // Renew goes to server_auth (not studio server)
        axios.post(`${getAuthUrl()}/auth/signin/renewtoken/`, { rt: result.rt, id: result.id, di: result.di, em: result.em })
          .then(({ data }) => {
            setAuth(data, loggedinuser);
          })
          .catch(function (result) {
            // setError(response.data.errors);
            // purgeAuth();
          });
      })
      // checkifuserisloggedin lehnt jetzt MIT Grund ab; ohne diesen Zweig
      // würde daraus eine unbehandelte Rejection statt einer Spur.
      .catch(function (err) {
        bootDiag('renewtoken.no-user-row', err)
      })
  }
  // ── Datenschutz-Zustimmung ────────────────────────────────────────────────
  //
  // Alle drei Aufrufe gehen an server_auth, nicht an den Studio-Server: die
  // Rechtstexte und das Zustimmungsprotokoll liegen in `mycrogym_core`, und
  // server_auth ist der einzige Dienst, den jede App beim Anmelden ohnehin
  // anspricht. Siehe server_auth/legal.js.

  /**
   * Geräteg gebundener Aufruf an `/auth/legal/*` mit einmaliger Token-Erneuerung.
   *
   * `verifyDevice` in server_auth prüft `at` mit `jwt.verify` und beantwortet
   * JEDEN Fehlschlag mit 401 „Token invalid" — ein abgelaufener Zugriffstoken
   * ist dort nicht von „Gerät unbekannt" zu unterscheiden. Beim Kaltstart liegt
   * in IndexedDB aber regelmäßig noch der Token der letzten Sitzung: der
   * Server-Worker erneuert erst, wenn sein Socket den ersten Fehlschlag sieht.
   * Ohne Wiederholung blieb der Zustimmungsstand für die ganze Sitzung
   * unbekannt — und `acceptConsent` warf, sperrte den Nutzer also hinter dem
   * Zustimmungsdialog ein, aus dem er nur per Neuanmeldung herauskam.
   */
  async function postLegal(path: string, body: Record<string, unknown> = {}) {
    const di = await JwtService.getDeviceId();
    const at = await JwtService.getToken();
    // Nur ein Klartext-JWT taugt: `at` liegt in IndexedDB verschlüsselt und wird
    // beim Lesen nur entschlüsselt, wenn der CryptoService bereit ist.
    if (!di || typeof at !== "string" || !at) throw new Error("Nicht angemeldet");
    try {
      return await axios.post(`${getAuthUrl()}${path}`, { ...body, di, at });
    } catch (e: any) {
      if (e?.response?.status !== 401) throw e;
      if (!(await renewTokenForWorker())) throw e;
      const fresh = await JwtService.getToken();
      return await axios.post(`${getAuthUrl()}${path}`, { ...body, di, at: fresh });
    }
  }

  /** Zustimmungsstand frisch vom Server holen (auch für Sitzungen ohne Login-Antwort). */
  async function refreshConsents(): Promise<LegalDocumentMeta[]> {
    const di = await JwtService.getDeviceId();
    const at = await JwtService.getToken();
    if (!di || !at) return pendingConsents.value;
    try {
      const { data } = await postLegal("/auth/legal/status");
      pendingConsents.value = Array.isArray(data?.required) ? data.required : [];
    } catch (e) {
      // Ein Fehler darf hier NICHT sperren: sonst legt ein kurzer Aussetzer des
      // Auth-Dienstes jede offene Sitzung lahm. Der Stand bleibt, wie er war.
      console.warn("[consent] Zustimmungsstand nicht abrufbar:", e);
    }
    return pendingConsents.value;
  }

  /** Den vollständigen Text einer Fassung laden (öffentlich, ohne Token). */
  async function loadLegalDocument(docType = "datenschutz", lang = "de") {
    const { data } = await axios.get(`${getAuthUrl()}/auth/legal/current`, {
      params: { doc_type: docType, lang },
    });
    return data as {
      document: LegalDocumentMeta & { body: string; bodyhash: string; requires_consent: boolean };
      checkbox_label: string;
    };
  }

  /** Zustimmung absenden. Antwortet mit dem verbleibenden offenen Rest. */
  async function acceptConsent(docId: string): Promise<LegalDocumentMeta[]> {
    const { data } = await postLegal("/auth/legal/accept", { doc_id: docId });
    pendingConsents.value = Array.isArray(data?.required) ? data.required : [];
    return pendingConsents.value;
  }

  return {
    errors,
    user,
    isAuthenticated,
    isLoggedin,
    login,
    loginprocess,
    pregenerateDeviceKeys,
    logout,
    register,
    forgotPassword,
    validateResetToken,
    verifyResetCode,
    resetPassword,
    loginPrefill,
    checkifuserisLoggedin,
    userisloggedincheckisrunning,
    verifyAuth,
    renewTokenForWorker,
    renewFailedTransiently,
    loggedinbyworker,
    pendingConsents,
    refreshConsents,
    loadLegalDocument,
    acceptConsent,
  };
});
